import { useCallback, useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { useAuth } from "../../../providers/AuthProvider";

export type BusinessProfilePayload = {
  store_type: string | null;
  profile_data: Record<string, Record<string, unknown>>;
  completed_steps: string[];
  completion_percentage: number;
  is_complete: boolean;
  next_step: string | null;
  updated_after_verification: boolean;
  last_updated_at: string | null;
};

export function useBusinessProfile() {
  const { token, refreshMe } = useAuth();
  const [profile, setProfile] = useState<BusinessProfilePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await api.businessProfile.get({ token });
      setProfile(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load profile");
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveStep = useCallback(
    async (step: string, body: Record<string, unknown>) => {
      if (!token) return;
      setSaving(true);
      setError(null);
      try {
        const res = await api.businessProfile.saveStep({ token, step, body });
        setProfile(res);
        if (res.is_complete) {
          await refreshMe(token);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to save step");
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [token, refreshMe],
  );

  const updateProfile = useCallback(
    async (opts: {
      profileData: Record<string, Record<string, unknown>>;
      storeType?: string | null;
    }) => {
      if (!token) return;
      setSaving(true);
      setError(null);
      try {
        const res = await api.businessProfile.update({
          token,
          profileData: opts.profileData,
          storeType: opts.storeType,
        });
        setProfile(res);
        await refreshMe(token);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to update profile");
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [token, refreshMe],
  );

  return { profile, error, loading, saving, reload: load, saveStep, updateProfile };
}
